import type { NuxtApp } from "#app";
import axios, { type AxiosInstance, type AxiosResponseHeaders, type RawAxiosResponseHeaders } from "axios";
import { client } from "laravel-precognition-vue";

export default defineNuxtPlugin({
  name: "axios",
  setup(nuxtApp) {
    const nuxt = nuxtApp as NuxtApp;
    const $localePath = useLocalePath();
    const isLocalBrowser = import.meta.dev && ["localhost", "127.0.0.1"].includes(location.hostname);
    const httpService: AxiosInstance = axios.create({
      baseURL: isLocalBrowser ? "/api-main" : nuxtApp.$config.public.baseUrl,
      withCredentials: true,
    });

    httpService.interceptors.request.use((config) => {
      (config as any).requestId = Math.random().toString(36).substring(2, 15);
      if (
        config.metadata?.showLoading ||
        config.method === "post" ||
        config.method === "put" ||
        config.method === "delete"
      ) {
        useUiStore().addRequest((config as any).requestId);
      }
      const locale = nuxt.$i18n.locale.value;
      const lang = locale || "en";
      const runtimeConfig = useRuntimeConfig();
      console.log({ runtimeConfig });
      config.withCredentials = true;
      config.params = {
        ...config.params,
        lang,
      };
      if (isLocalBrowser && import.meta.env.VITE_BEARER_TOKEN) {
        config.headers.Authorization = `Bearer ${import.meta.env.VITE_BEARER_TOKEN}`;
      }
      config.headers.set("X-Timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
      return config;
    });
    function validateErrorHeaders(headers: RawAxiosResponseHeaders | AxiosResponseHeaders) {
      if (headers["x-password-required"]) {
        useAuthStore().setRequirePasswordConfirmation(true);
      }
      const authErrors = headers["x-auth-errors"];
      console.log({ authErrors });
      if (authErrors) {
        const errorsList = String(authErrors).split(",");
        const errorRoutes: Record<string, string> = {
          HasPassword: $localePath("/create-password"),
          TwoFactorAuthPending: $localePath("/two-factor-code"),
          MustBeVerified: $localePath("/email-confirmation"),
          MustFinishRegistration: $localePath("/register"),
          MustAcceptTerms: $localePath("/register"),
          // MustSelectInterests: $localePath('/select-interests'),
        };

        const priorityOrder = [
          "HasPassword",
          "TwoFactorAuthPending",
          "MustBeVerified",
          "MustFinishRegistration",
          "MustAcceptTerms",
          // 'MustSelectInterests',
        ];

        let authPriorityError: string | null = null;
        for (const error of priorityOrder) {
          if (errorsList.includes(error)) {
            authPriorityError = error;
            break;
          }
        }

        if (authPriorityError && errorRoutes[authPriorityError]) {
          if (authPriorityError === "MustFinishRegistration") {
            useAuthStore().setMissingStep("2");
          } else if (authPriorityError === "MustAcceptTerms") {
            useAuthStore().setMissingStep("3");
          }

          if (location.pathname.match(new RegExp(`^${errorRoutes[authPriorityError]}.*?`))) {
            return;
          }
          // href + search, not pathname: assigning to pathname drops the query
          // string, and system_id/redirect_to live there. Losing them is why a
          // user bounced here mid-flow ended up attributed to MGN and landed on
          // the dashboard instead of back on the service they came from.
          window.location.href = `${errorRoutes[authPriorityError]}${location.search}`;
        }
      }
    }
    httpService.interceptors.response.use(
      function (response) {
        useUiStore().requestFinished((response.config as any).requestId);
        validateErrorHeaders(response.headers);

        // Also check response body for status (e.g., from login endpoint)
        if (response.data?.status) {
          const status = response.data.status;
          const $localePath = useLocalePath();

          // Same reason as above: keep the query when leaving for /register.
          const registerUrl = `${$localePath("/register")}${location.search}`;
          if (status === "MustFinishRegistration") {
            useAuthStore().setMissingStep("2");
            window.location.href = registerUrl;
          } else if (status === "MustAcceptTerms") {
            useAuthStore().setMissingStep("3");
            window.location.href = registerUrl;
          }
        }

        return response;
      },
      function (error) {
        if (error.config?.requestId) {
          useUiStore().requestFinished(error.config.requestId);
        }
        // isLocalBrowser (not appEnv === "local") — appEnv reflects whatever
        // APP_ENV the build happened to carry, which can leak "local" into a
        // real deployment (see isLocalDevBuild in nuxt.config.ts); this alert
        // telling someone to run a local CLI command must never surface on
        // an actual deployed environment just because of that.
        if (error.response?.status === 401 && isLocalBrowser) {
          alert(
            "Invalid token, please remove the token from the .env file by running npm run mgn:logout and npm run mgn:login",
          );
        }
        if (error.response?.headers) {
          validateErrorHeaders(error.response.headers);
        }
        return Promise.reject(error);
      },
    );
    client.use(httpService);
    nuxtApp.provide("axios", httpService);
  },
});
